import {
  breakpointSizes,
  ButtonDesigns,
  ButtonTypes,
  getBreakpointDown,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
  TopLevelError,
} from '@paywithextend/components'
import BaseModal, {
  BaseModalProps,
} from 'ext-ui/components/BaseModal/BaseModal'
import React from 'react'
import { useSelector } from 'react-redux'
import Button from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import {
  FormFieldsValidation,
  useFlyoutDispatch,
  useFormFields,
} from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { connectedPush } from 'src/navigation/Lnk'
import { expenseCategoryLabelsUrl } from 'src/navigation/urls'
import {
  PostExpenseCategoryRequest,
  usePostExpenseCategory,
} from 'src/queries/organizations/expenseCategories.queries'
import { selectOrganizationIdFromRoute } from 'src/redux/selectors/data/organizations.selectors'
import { parseBackendErrors } from 'src/utils/error.utils'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

type CreateExpenseCategoryModalProps = BaseModalProps & {}

const CreateExpenseCategoryModal: React.FC<CreateExpenseCategoryModalProps> = ({
  isOpen,
  onClose,
}) => {
  const translate = useTranslate()
  const dispatch = useFlyoutDispatch()

  // assumes that this popup is being called from organizationSettings route
  const orgId = useSelector(selectOrganizationIdFromRoute)

  const {
    mutate: postNewExpCat,
    isPending: postingNewExpCat,
    error: postErrors,
  } = usePostExpenseCategory()
  const topLevelError = postErrors
    ? parseBackendErrors(postErrors).topLevelError
    : undefined

  const {
    fields: newExpCat,
    setFields: setNewExpCat,
    fieldsErrors: newExpCatErrors,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
  } = useFormFields<PostExpenseCategoryRequest>(
    {
      code: '',
      name: '',
    },
    (): FormFieldsValidation<PostExpenseCategoryRequest> => ({
      code: [stringIsEmpty()],
      name: [stringIsEmpty()],
    }),
  )

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    if (flatFieldsSetErrors()) {
      postNewExpCat(
        { body: newExpCat },
        {
          onSuccess: (res) => {
            onClose()
            dispatch(connectedPush(expenseCategoryLabelsUrl(orgId, res.id)))
          },
        },
      )
    }
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      variant="interactive"
      aria-label={translate('Create Expense Category Modal')}
    >
      <form
        onSubmit={onSubmit}
        css={css`
          position: relative;
          display: flex;
          flex-direction: column;
          padding: 40px 60px;

          ${getBreakpointDown(breakpointSizes.XSM)(css`
            padding: 0 20px;
          `)}
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            ${getFontCss(
              24,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            margin-bottom: 20px;
          `}
        >
          {translate('New Expense Category')}
        </div>
        <div
          css={css`
            display: flex;
            gap: 20px;
            width: 100%;
            ${getBreakpointDown(breakpointSizes.XSM)(css`
              flex-direction: column;
            `)}
          `}
        >
          <TextInputField
            css={css`
              width: 34%;
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                width: 100%;
              `)}
            `}
            design={TextInputFieldDesigns.New}
            fieldLabel={translate('Code')}
            value={newExpCat.code}
            onChange={(val: string): void => {
              setNewExpCat({ ...newExpCat, code: val })
            }}
            placeholder={translate('Enter a code')}
            testId={'expCatCodeInput'}
            errors={newExpCatErrors.code}
          />
          <TextInputField
            css={css`
              flex-grow: 2;
            `}
            design={TextInputFieldDesigns.New}
            fieldLabel={translate('Expense Category Name')}
            value={newExpCat.name}
            onChange={(val: string): void => {
              setNewExpCat({ ...newExpCat, name: val })
            }}
            placeholder={translate('Enter a name')}
            testId={'expCatNameInput'}
            errors={newExpCatErrors.name}
          />
        </div>
        {topLevelError !== undefined && (
          <TopLevelError
            css={css`
              ${getFontCss(
                12,
                styleConstants.colors.red,
                styleConstants.fontFamilies.medium,
              )};
              margin-top: 12px;
            `}
            error={topLevelError}
          />
        )}
        <div
          css={css`
            display: flex;
            gap: 20px;
            margin-top: 40px;

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              flex-direction: column;
              align-items: center;
              width: 100%;
            `)}
          `}
        >
          <Button
            testId={'createBtn'}
            type={ButtonTypes.submit}
            design={ButtonDesigns.designCta}
            big={true}
            text={translate('Create Category')}
            loading={postingNewExpCat}
            loadingText={translate('Creating Category...')}
            css={css`
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                width: 100%;
              `)}
            `}
          />
          <HoverableLink
            onClick={onClose}
            ariaLabel={'Cancel'}
            size={HoverableLinkSizes.Medium}
            testId={'cancelBtn'}
          >
            {translate('Cancel')}
          </HoverableLink>
        </div>
      </form>
    </BaseModal>
  )
}

export default CreateExpenseCategoryModal
